import * as React from 'react';
import { FaUmbrellaBeach } from 'react-icons/fa';
import { colors } from '../../employees/styles/personalakteStyles';
import { AbsenceCalendarDay } from '../services/ShiftAnalyticsService';

interface AbsenceCalendarProps {
  days: AbsenceCalendarDay[];
  employeeCount: number;
  label: string;
}

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MAX_PILLS = 4;

const ABSENCE_BG = '#3b82f6';
const EVENT_BG: Record<string, string> = {
  holiday: '#8b5cf6',
  event: '#f59e0b',
};

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// ISO-8601 week number (Monday-first, week 1 contains the first Thursday).
function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
}

function Pill({ text, bg }: { text: string; bg: string }): React.ReactElement {
  return (
    <div
      title={text}
      style={{
        background: bg, color: '#fff',
        fontSize: '10px', fontWeight: 600, lineHeight: 1.5,
        padding: '0 5px', borderRadius: '4px',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {text}
    </div>
  );
}

export default function AbsenceCalendar({ days, employeeCount, label }: AbsenceCalendarProps): React.ReactElement {
  const totalAbsentDays = days.reduce((acc, d) => acc + d.count, 0);
  const isWeekend = (iso: string) => {
    const wd = parseLocalDate(iso).getDay();
    return wd === 0 || wd === 6;
  };

  const leadingBlanks = days.length > 0
    ? (parseLocalDate(days[0].date).getDay() + 6) % 7 // Monday-first
    : 0;
  const cells: Array<AbsenceCalendarDay | null> = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...days,
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: Array<Array<AbsenceCalendarDay | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(226, 232, 240, 0.9)',
      borderRadius: '14px',
      padding: '16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{
          width: '34px', height: '34px', borderRadius: '9px',
          background: 'rgba(59, 130, 246, 0.1)', color: ABSENCE_BG,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0,
        }}>
          <FaUmbrellaBeach />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>Absence Calendar</div>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>Wer ist wann abwesend · Feiertage &amp; Events · {label}</div>
        </div>
        <div style={{
          fontSize: '11px', fontWeight: 700, color: ABSENCE_BG,
          background: 'rgba(59, 130, 246, 0.08)', padding: '4px 9px', borderRadius: '8px',
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {totalAbsentDays} Fehltage
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '26px repeat(7, 1fr)', gap: '4px' }}>
        <div />
        {WEEKDAYS.map((wd, i) => (
          <div key={i} style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textAlign: 'center', paddingBottom: '2px' }}>
            {wd}
          </div>
        ))}

        {weeks.map((week, wi) => {
          const firstDay = week.find(Boolean) as AbsenceCalendarDay | undefined;
          const kw = firstDay ? isoWeek(parseLocalDate(firstDay.date)) : '';
          return (
            <React.Fragment key={wi}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '6px', fontSize: '9px', fontWeight: 700, color: '#cbd5e1' }}>
                {kw}
              </div>
              {week.map((cell, di) => {
                if (!cell) return <div key={`b-${wi}-${di}`} style={{ background: '#fbfcfe', borderRadius: '8px' }} />;
                const dayNum = parseLocalDate(cell.date).getDate();
                const weekend = isWeekend(cell.date);
                const pills = [
                  ...cell.events.map((ev) => ({ text: ev.name, bg: EVENT_BG[ev.type] || EVENT_BG.event })),
                  ...cell.names.map((n) => ({ text: n, bg: ABSENCE_BG })),
                ];
                const shown = pills.slice(0, MAX_PILLS);
                const extra = pills.length - shown.length;
                return (
                  <div
                    key={cell.date}
                    style={{
                      minHeight: '92px',
                      borderRadius: '8px',
                      background: weekend ? '#f8fafc' : '#fff',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      padding: '4px',
                      display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden',
                    }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: 700, color: weekend ? '#94a3b8' : '#475569', paddingLeft: '2px' }}>
                      {dayNum}
                    </div>
                    {shown.map((p, i) => <Pill key={i} text={p.text} bg={p.bg} />)}
                    {extra > 0 && (
                      <div style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', paddingLeft: '2px' }}>+{extra} mehr</div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '12px', fontSize: '10px', color: '#94a3b8', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: ABSENCE_BG }} /> abwesend
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: EVENT_BG.holiday }} /> Feiertag
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: EVENT_BG.event }} /> Event
        </span>
        <span style={{ marginLeft: 'auto' }}>{employeeCount} MA im Team</span>
      </div>
    </div>
  );
}
